/**
 * Roman -> Indic script transliteration, best-effort.
 *
 * This is NOT translation - a name has no meaning to translate, only a sound
 * to carry into another script. All nine scripts here (Devanagari, Gujarati,
 * Bengali, Gurmukhi, Odia, Tamil, Telugu, Kannada, Malayalam) descend from
 * the same Brahmi ancestor and - not by coincidence - Unicode lays each of
 * them out as a near-exact parallel of Devanagari's block, letter for
 * letter, at the same relative offset from each script's own base
 * codepoint. That single fact is what lets one phonetic table drive all
 * nine: build the sound as a Devanagari-relative "slot", then re-base it
 * onto whichever script the card actually needs.
 *
 * It is approximate by construction. Casual Roman spelling of Indian names
 * is not a phonetic code - the same "a" stands for a schwa in one name and
 * an open "aa" in the next, and no rule recovers which without already
 * knowing the name. This picks the common, literal convention (doubled
 * letters = long vowels, single = short) and stays consistent; it will not
 * always match how a given name is traditionally spelled by hand. Tamil,
 * lacking separate letters for aspirated/voiced consonants that the other
 * scripts have, folds those down to the nearest letter it does have -
 * expected and correct for Tamil, not a bug.
 */

const VIRAMA = 0x4d;

// Longest-match-first. Order matters: "chh" before "ch" before "c", etc.
const CONSONANTS = [
  ["chh", 0x1b], ["sh", 0x36], ["ch", 0x1a], ["th", 0x25], ["dh", 0x27],
  ["kh", 0x16], ["gh", 0x18], ["ph", 0x2b], ["bh", 0x2d],
  ["ng", 0x19], ["ny", 0x1e], ["jh", 0x1d],
  ["k", 0x15], ["g", 0x17], ["c", 0x1a], ["j", 0x1c],
  ["t", 0x24], ["d", 0x26], ["n", 0x28],
  ["p", 0x2a], ["f", 0x2b], ["b", 0x2c], ["m", 0x2e],
  ["y", 0x2f], ["r", 0x30], ["l", 0x32], ["v", 0x35], ["w", 0x35],
  ["s", 0x38], ["h", 0x39], ["x", 0x38], ["z", 0x1c], ["q", 0x15],
];

// [roman, independent-vowel offset, matra offset (null = inherent, no mark)]
// "ee"/"oo" are the common English spelling of the long ii/uu sounds (Meera,
// Poonam) and must be checked before the single-letter "e"/"o" - without
// them "Meera" parses as m-EE(vowel)-E(second, now-orphaned vowel)-ra and
// the stray mid-word vowel corrupts the whole word.
const VOWELS = [
  ["aa", 0x06, 0x3e], ["ee", 0x08, 0x40], ["oo", 0x0a, 0x42],
  ["ii", 0x08, 0x40], ["uu", 0x0a, 0x42],
  ["ai", 0x10, 0x48], ["au", 0x14, 0x4c],
  // English "oa" (Road, Coach) is one long-o sound, same target as plain
  // "o" - without this it reads as two vowels and inserts a phantom
  // syllable, which matters for addresses full of English loanwords.
  ["oa", 0x13, 0x4b],
  ["a", 0x05, null], ["i", 0x07, 0x3f], ["u", 0x09, 0x41],
  ["e", 0x0f, 0x47], ["o", 0x13, 0x4b],
];

/** Per-script base codepoint, and a fallback for any slot it doesn't have a
    dedicated letter for - re-mapped to the nearest slot every script here
    does have, so nothing ever renders as a missing-glyph box. */
const SCRIPTS = {
  devanagari: { base: 0x0900, fallback: {} },
  gujarati: { base: 0x0a80, fallback: {} },
  bengali: { base: 0x0980, fallback: {} },
  gurmukhi: { base: 0x0a00, fallback: { 0x36: 0x38 } }, // sh -> s
  odia: { base: 0x0b00, fallback: {} },
  telugu: { base: 0x0c00, fallback: {} },
  kannada: { base: 0x0c80, fallback: {} },
  malayalam: { base: 0x0d00, fallback: {} },
  tamil: {
    base: 0x0b80,
    // Tamil writes one letter per place of articulation, not per
    // voicing/aspiration - kh/g/gh all fold to the k-letter, etc. sh/ssa/s/h
    // are NOT in this list: Tamil has them natively (borrowed from Grantha
    // specifically for Sanskrit-origin words - exactly the case for a name).
    fallback: {
      0x16: 0x15, 0x17: 0x15, 0x18: 0x15, // kh, g, gh -> k
      0x1b: 0x1a, 0x1d: 0x1c, // chh -> ch; jh -> j (Tamil does have j, via Grantha)
      0x20: 0x1f, 0x21: 0x1f, 0x22: 0x1f, // tth, dd, ddh -> tt (retroflex)
      0x25: 0x24, 0x26: 0x24, 0x27: 0x24, // th, d, dh -> t
      0x2b: 0x2a, 0x2c: 0x2a, 0x2d: 0x2a, // ph, b, bh -> p
    },
  },
};

function matchLongest(table, text, pos) {
  for (const entry of table) {
    const roman = entry[0];
    if (text.startsWith(roman, pos)) return entry;
  }
  return null;
}

function slotsFor(word) {
  const text = word.toLowerCase();
  const slots = [];
  let pos = 0;
  let expectVowel = true; // true at word start, or right after another vowel

  while (pos < text.length) {
    const ch = text[pos];
    if (!/[a-z]/.test(ch)) {
      slots.push({ literal: ch });
      pos += 1;
      expectVowel = true;
      continue;
    }

    if (expectVowel) {
      const v = matchLongest(VOWELS, text, pos);
      if (v) {
        slots.push({ vowel: v[1] });
        pos += v[0].length;
        expectVowel = true;
        continue;
      }
    }

    const c = matchLongest(CONSONANTS, text, pos);
    if (c) {
      pos += c[0].length;
      const v = matchLongest(VOWELS, text, pos);
      if (v) {
        slots.push({ consonant: c[1], matra: v[2] });
        pos += v[0].length;
        expectVowel = true;
      } else {
        // No vowel follows. Mid-word, another consonant is coming next and
        // needs a virama to suppress this one's inherent vowel; at the end
        // of the word the inherent vowel stays (unmarked, as Devanagari
        // spelling conventionally keeps it even where speech elides it).
        const nextIsConsonant = pos < text.length && /[a-z]/.test(text[pos]);
        slots.push({ consonant: c[1], matra: undefined, cluster: nextIsConsonant });
        expectVowel = false;
      }
      continue;
    }

    // A letter neither table recognises (rare in practice). Skip rather
    // than guess wrong.
    pos += 1;
  }

  return slots;
}

function render(slots, scriptKey) {
  const script = SCRIPTS[scriptKey];
  if (!script) return null;

  const at = (offset) => {
    const mapped = script.fallback[offset] ?? offset;
    return String.fromCodePoint(script.base + mapped);
  };

  let out = "";
  slots.forEach((slot, i) => {
    if (slot.literal !== undefined) {
      out += slot.literal;
      return;
    }
    if (slot.vowel !== undefined) {
      out += at(slot.vowel);
      return;
    }
    out += at(slot.consonant);
    if (slot.matra) out += at(slot.matra);
    if (slot.cluster) out += String.fromCodePoint(script.base + VIRAMA);
  });
  return out;
}

/** Transliterates one word or short phrase into the given script. Unknown
    scripts, or input with nothing alphabetic in it, return null. */
export function transliterateWord(text, scriptKey) {
  const trimmed = String(text || "").trim();
  if (!trimmed || !SCRIPTS[scriptKey]) return null;
  return render(slotsFor(trimmed), scriptKey);
}

/** Same, word by word, so multi-word names and addresses keep their
    spacing and punctuation untouched. */
export function transliterateText(text, scriptKey) {
  const trimmed = String(text || "").trim();
  if (!trimmed || !SCRIPTS[scriptKey]) return null;
  return trimmed
    .split(/(\s+)/)
    .map((chunk) => (/\s/.test(chunk) ? chunk : render(slotsFor(chunk), scriptKey) ?? chunk))
    .join("");
}

export const SUPPORTED_SCRIPTS = Object.keys(SCRIPTS);
